import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { GoogleMap, Marker, Polyline, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { carpoolsAPI, scheduleAPI } from '../services/api';
import io from 'socket.io-client';
import { PageLoading } from '../components/appPage';
import './Tracking.css';

const LIBRARIES = ['places', 'geometry'];

const Tracking = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { carpoolId } = useParams();
  const [carpool, setCarpool] = useState(null);
  const [rides, setRides] = useState([]);
  const [selectedRide, setSelectedRide] = useState(null);
  const [driverLocations, setDriverLocations] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 42.6977, lng: 23.3219 }); // Sofia default

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES
  });

  // Load carpool and rides data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [carpoolRes, ridesRes] = await Promise.all([
          carpoolsAPI.getCarpool(carpoolId),
          scheduleAPI.getSchedule(carpoolId)
        ]);

        setCarpool(carpoolRes.data.carpool);
        setRides(ridesRes.data.rides || []);

        // Set map center to carpool location if available
        if (carpoolRes.data.carpool?.startLatitude && carpoolRes.data.carpool?.startLongitude) {
          setMapCenter({
            lat: carpoolRes.data.carpool.startLatitude,
            lng: carpoolRes.data.carpool.startLongitude
          });
        }
      } catch (error) {
        console.error('Failed to load tracking data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [carpoolId]);

  // Socket connection for real-time driver tracking
  useEffect(() => {
    if (!user?.isPremium || !carpoolId) return;

    const token = localStorage.getItem('accessToken');
    const newSocket = io(process.env.REACT_APP_API_URL || 'http://localhost:5000', {
      auth: { token }
    });

    newSocket.on('connect', () => {
      console.log('Connected to tracking socket');
      newSocket.emit('join-carpool-tracking', carpoolId);
    });

    newSocket.on('driver-location', (data) => {
      setDriverLocations(prev => ({
        ...prev,
        [data.userId]: {
          lat: data.latitude,
          lng: data.longitude,
          driverName: data.driverName,
          timestamp: data.timestamp
        }
      }));
    });

    setSocket(newSocket);

    return () => {
      newSocket.emit('leave-carpool-tracking', carpoolId);
      newSocket.close();
    };
  }, [user, carpoolId]);

  // Get today's rides
  const todayRides = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return rides.filter(ride => {
      const rideDate = new Date(ride.scheduledDate).toISOString().split('T')[0];
      return rideDate === today;
    });
  }, [rides]);

  // If current user is today's assigned driver, publish live GPS updates
  useEffect(() => {
    if (!socket || !user?.id || !todayRides.length) return;
    if (!navigator.geolocation) return;

    const activeRide = todayRides.find((ride) => {
      const isAssignedDriver = ride.driver?.id === user.id;
      const isActiveStatus = ride.status !== 'completed' && ride.status !== 'cancelled';
      return isAssignedDriver && isActiveStatus;
    });

    if (!activeRide) return;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const payload = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy || undefined,
          rideId: activeRide.id,
        };

        socket.emit('location-update', payload);

        // Optimistic local update for smoother UX until socket broadcast returns
        setDriverLocations((prev) => ({
          ...prev,
          [user.id]: {
            lat: payload.latitude,
            lng: payload.longitude,
            driverName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Вие',
            timestamp: new Date().toISOString(),
          },
        }));
      },
      (error) => {
        console.warn('Geolocation watch failed:', error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 15000,
      }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, [socket, todayRides, user]);

  // Get all unique pickup points from today's rides
  const pickupPoints = useMemo(() => {
    const points = [];
    const seen = new Set();

    (selectedRide ? [selectedRide] : todayRides).forEach(ride => {
      if (ride.pickupPoint?.latitude && ride.pickupPoint?.longitude) {
        const key = `${ride.pickupPoint.latitude},${ride.pickupPoint.longitude}`;
        if (!seen.has(key)) {
          seen.add(key);
          points.push({
            id: ride.pickupPoint.id,
            position: {
              lat: ride.pickupPoint.latitude,
              lng: ride.pickupPoint.longitude
            },
            address: ride.pickupPoint.address,
            childName: ride.child?.name,
            rideTime: ride.scheduledTime,
            type: 'pickup'
          });
        }
      }
    });

    return points;
  }, [todayRides, selectedRide]);

  // Destination point
  const destination = useMemo(() => {
    if (!carpool) return null;

    // Use end address if available, otherwise start address (school/activity location)
    const lat = carpool.endLatitude || carpool.startLatitude;
    const lng = carpool.endLongitude || carpool.startLongitude;
    const address = carpool.endAddress || carpool.startAddress;

    if (!lat || !lng) return null;

    return {
      position: { lat, lng },
      address,
      name: carpool.name,
      type: 'destination'
    };
  }, [carpool]);

  // Route path (pickup points + destination)
  const routePath = useMemo(() => {
    const path = [];

    // Sort pickup points by some logic (could be optimized with actual routing)
    pickupPoints.forEach(point => {
      path.push(point.position);
    });

    // Add destination at the end
    if (destination) {
      path.push(destination.position);
    }

    return path;
  }, [pickupPoints, destination]);

  // Custom marker icons
  const getMarkerIcon = useCallback((type) => {
    if (!isLoaded || !window.google) return null;

    const icons = {
      pickup: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#10b981',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3
      },
      destination: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 14,
        fillColor: '#ef4444',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3
      },
      driver: {
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        scale: 1.5,
        fillColor: '#667eea',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 1,
        anchor: new window.google.maps.Point(12, 22)
      }
    };

    return icons[type] || icons.pickup;
  }, [isLoaded]);

  // Map options
  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ]
  }), []);

  // Polyline options
  const polylineOptions = useMemo(() => ({
    strokeColor: '#667eea',
    strokeOpacity: 0.8,
    strokeWeight: 4,
    geodesic: true
  }), []);

  if (!user?.isPremium) {
    return (
      <div className="tracking-page page-shell">
        <div className="tracking-header">
          <Link to={`/carpools/${carpoolId}`} className="back-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Назад
          </Link>
          <h1>Проследяване</h1>
        </div>
        <div className="premium-required">
          <div className="premium-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </div>
          <h2>Premium функция</h2>
          <p>Проследяването в реално време е налично само за Premium потребители.</p>
          <button className="btn btn-primary btn-lg">
            Upgrade към Premium
          </button>
        </div>
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div className="tracking-page page-shell">
        <div className="tracking-header">
          <h1>Проследяване</h1>
        </div>
        <div className="error-message">
          <p>Google Maps API ключ липсва. Моля добавете REACT_APP_GOOGLE_MAPS_API_KEY в .env файла.</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="tracking-page page-shell">
        <div className="tracking-header">
          <h1>Проследяване</h1>
        </div>
        <div className="error-message">
          <p>Грешка при зареждане на картата: {loadError.message}</p>
          <p style={{ marginTop: '0.75rem', fontSize: '0.95rem', lineHeight: 1.5 }}>
            Ако виждате „This page can’t load Google Maps correctly“: проверете свързан <strong>Billing</strong> към
            проекта в Google Cloud, включени <strong>Maps JavaScript API</strong>, <strong>Places API</strong> и{' '}
            <strong>Geocoding API</strong>, и HTTP referrer за <code>localhost</code> / домейна. Пълен чеклист:{' '}
            <code>docs/GOOGLE_MAPS_SETUP.md</code>
          </p>
        </div>
      </div>
    );
  }

  if (loading || !isLoaded) {
    return (
      <div className="tracking-page page-shell">
        <div className="tracking-header">
          <h1>Проследяване</h1>
        </div>
        <PageLoading message={t('tracking.loadingMap', 'Зареждане на картата...')} />
      </div>
    );
  }

  return (
    <div className="tracking-page page-shell">
      <div className="tracking-header">
        <div className="tracking-header-left">
          <Link to={`/carpools/${carpoolId}`} className="back-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Назад
          </Link>
          <h1>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            Проследяване - {carpool?.name}
          </h1>
        </div>
      </div>

      <div className="tracking-content">
        {/* Sidebar with ride info */}
        <div className="tracking-sidebar">
          <div className="sidebar-section">
            <h3>Днешни пътувания ({todayRides.length})</h3>
            {todayRides.length === 0 ? (
              <p className="no-rides">Няма пътувания за днес</p>
            ) : (
              <div className="rides-list">
                {todayRides.map(ride => (
                  <div
                    key={ride.id}
                    className={`ride-item ${selectedRide?.id === ride.id ? 'selected' : ''}`}
                    onClick={() => setSelectedRide(selectedRide?.id === ride.id ? null : ride)}
                  >
                    <div className="ride-time">{ride.scheduledTime}</div>
                    <div className="ride-details">
                      <span className="child-name">{ride.child?.name}</span>
                      {ride.driver && (
                        <span className="driver-name">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          {ride.driver.firstName} {ride.driver.lastName}
                        </span>
                      )}
                    </div>
                    <div className={`ride-status status-${ride.status}`}>
                      {ride.status === 'scheduled' ? 'Планирано' :
                       ride.status === 'inProgress' ? 'В движение' :
                       ride.status === 'completed' ? 'Завършено' : ride.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sidebar-section">
            <h3>Легенда</h3>
            <div className="legend">
              <div className="legend-item">
                <span className="legend-marker pickup"></span>
                <span>Точка за качване</span>
              </div>
              <div className="legend-item">
                <span className="legend-marker destination"></span>
                <span>Дестинация</span>
              </div>
              <div className="legend-item">
                <span className="legend-marker driver"></span>
                <span>Шофьор (live)</span>
              </div>
            </div>
          </div>

          {/* Active drivers */}
          {Object.keys(driverLocations).length > 0 && (
            <div className="sidebar-section">
              <h3>Активни шофьори</h3>
              <div className="active-drivers">
                {Object.entries(driverLocations).map(([userId, location]) => (
                  <div key={userId} className="driver-item">
                    <div className="driver-avatar">
                      {location.driverName?.charAt(0) || '?'}
                    </div>
                    <div className="driver-info">
                      <span className="driver-name">{location.driverName}</span>
                      <span className="driver-status">
                        <span className="live-indicator"></span>
                        В движение
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="tracking-map">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={13}
            options={mapOptions}
          >
            {/* Route polyline */}
            {routePath.length > 1 && (
              <Polyline
                path={routePath}
                options={polylineOptions}
              />
            )}

            {/* Pickup point markers */}
            {pickupPoints.map((point, index) => (
              <Marker
                key={`pickup-${point.id || index}`}
                position={point.position}
                icon={getMarkerIcon('pickup')}
                onClick={() => setSelectedMarker(point)}
                label={{
                  text: String(index + 1),
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}
              />
            ))}

            {/* Destination marker */}
            {destination && (
              <Marker
                position={destination.position}
                icon={getMarkerIcon('destination')}
                onClick={() => setSelectedMarker(destination)}
              />
            )}

            {/* Driver markers */}
            {Object.entries(driverLocations).map(([userId, location]) => (
              <Marker
                key={`driver-${userId}`}
                position={{ lat: location.lat, lng: location.lng }}
                icon={getMarkerIcon('driver')}
                onClick={() => setSelectedMarker({
                  ...location,
                  type: 'driver',
                  driverName: location.driverName
                })}
              />
            ))}

            {/* Info window */}
            {selectedMarker && (
              <InfoWindow
                position={selectedMarker.position || { lat: selectedMarker.lat, lng: selectedMarker.lng }}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div className="info-window">
                  {selectedMarker.type === 'pickup' && (
                    <>
                      <h4>{selectedMarker.childName}</h4>
                      <p>{selectedMarker.address}</p>
                      <p className="info-time">Час: {selectedMarker.rideTime}</p>
                    </>
                  )}
                  {selectedMarker.type === 'destination' && (
                    <>
                      <h4>{selectedMarker.name}</h4>
                      <p>{selectedMarker.address}</p>
                    </>
                  )}
                  {selectedMarker.type === 'driver' && (
                    <>
                      <h4>{selectedMarker.driverName}</h4>
                      <p className="live-status">
                        <span className="live-indicator"></span>
                        Активен
                      </p>
                    </>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>
      </div>
    </div>
  );
};

export default Tracking;
